import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import pLimit from "p-limit";

import { FileUploadStatus } from "components/bid_module/types";
import { MAX_PARALLEL_DOCUMENT_UPLOADS, useVariableValue } from "lib/featureFlags";
import { CustomerDocumentType } from "lib/types/graphQLEnums";
import { useCreateCustomerDocument } from "./useCreateCustomerDocument";
import { useCustomerDocumentProcessingStatus } from "./useCustomerDocumentProcessingStatus";
import { useStoreCustomerDocument } from "./useStoreCustomerDocument";

type UploadFilesInput = {
  files: File[];
  documentType: CustomerDocumentType;
  uiPath?: string;
  resourceId?: string;
  uploadStatuses: FileUploadStatus[];
  filesProcessing: boolean;
  updateFileStatus: ({
    fileName,
    documentId,
    updates,
  }: {
    fileName: string | null | undefined;
    documentId: string | null | undefined;
    updates: Partial<FileUploadStatus>;
  }) => void;
};

/**
 * Handles the upload and creation of a customer document. Uploads the document
 * to an s3 bucket, handles the creation of the customer document in the
 * database, as well as its processing (chunking & indexing)
 * @param param0
 * @returns
 */
export function useUploadCustomerDocuments({
  files,
  resourceId,
  uiPath,
  documentType,
  uploadStatuses,
  filesProcessing,
  updateFileStatus,
}: UploadFilesInput) {
  // Subscription which handles the processing status of customer documents
  // and updates their status in real time
  useCustomerDocumentProcessingStatus({
    resourceId,
    updateFileStatus,
    documentType,
    enabled: filesProcessing,
  });

  const { mutateAsync: storeCustomerDocument } = useStoreCustomerDocument();
  const { mutateAsync: createCustomerDocument } = useCreateCustomerDocument();

  const maxParallelDocumentUploads = useVariableValue(MAX_PARALLEL_DOCUMENT_UPLOADS, 1);

  return useMutation<void, AxiosError>({
    mutationFn: async () => {
      const limit = pLimit(maxParallelDocumentUploads);

      const processFile = async (file: File) => {
        const currentFileStatus = uploadStatuses.find((status) => status.file.name === file.name);
        if (currentFileStatus?.isUploaded && currentFileStatus.isProcessed) {
          return;
        }
        try {
          const s3DocumentId = await storeCustomerDocument({
            file,
            resourceId,
            onProgress: (progress) => {
              // At this point, we don't have the documentId yet, so we can only update via file name
              updateFileStatus({
                fileName: file.name,
                documentId: undefined,
                updates: {
                  uploadProgress: progress,
                  isUploaded: progress >= 100,
                  isProcessed: false,
                  isError: false,
                },
              });
            },
          });

          // Mutation kicks off useCustomerDocumentProcessingStatus subscription and stores the document metadata in customer_documents table
          const { createCustomerDocument: response } = await createCustomerDocument({
            request: {
              resourceId,
              fileName: file.name,
              s3DocumentId,
              metadata: {
                fileSizeBytes: file.size,
                mimeType: file.type,
              },
              documentType,
              uiPath,
            },
          });

          // Update file status with document ID - preferred way to track and update fileStatus
          updateFileStatus({
            fileName: file.name,
            documentId: null,
            updates: {
              isUploaded: true,
              documentId: response.docId,
              isProcessed: false,
              isError: false,
            },
          });
        } catch (error) {
          updateFileStatus({
            fileName: file.name,
            documentId: undefined,
            updates: { isError: true, isFinished: true, isProcessed: false },
          });
          // Throw error up the Promise.all chain
          throw error;
        }
      };

      await Promise.all(files.map((file) => limit(() => processFile(file))));
    },
  });
}
